import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Navigate } from "react-router-dom";
import { platformApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, LogOut, LayoutDashboard, Building2, Users, Archive, Mail, FileText } from "lucide-react";

const BG = "min-h-screen bg-slate-950 text-slate-100";

export function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("jb_platform_token")) nav("/admin", { replace: true });
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await platformApi.post("/platform/auth/login", { email, password });
      localStorage.setItem("jb_platform_token", data.access_token);
      localStorage.setItem("jb_platform_user", JSON.stringify(data.user));
      toast.success("Welcome to JobsBook Platform");
      nav("/admin", { replace: true });
    } catch (e2) {
      toast.error(formatApiError(e2?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className={`${BG} flex items-center justify-center px-6`}>
      <form onSubmit={submit} className="w-full max-w-md bg-slate-900/80 backdrop-blur border border-slate-800 rounded-[10px] p-8 shadow-2xl"
            data-testid="platform-login-form">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="h-7 w-7 text-rose-400" />
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-rose-400">// JobsBook Platform</div>
            <h1 className="text-2xl font-black tracking-tight">Super-admin sign in</h1>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} className="bg-slate-800 border-slate-700 text-slate-100" data-testid="platform-login-email" autoFocus />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-400">Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-slate-800 border-slate-700 text-slate-100" data-testid="platform-login-password" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-rose-600 hover:bg-rose-700 text-white" data-testid="platform-login-submit">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
          <p className="text-[11px] text-slate-500 pt-2">For platform administrators only. Manager sign-in lives at <code className="text-slate-300">/login</code>.</p>
        </div>
      </form>
    </div>
  );
}

export function AdminShell() {
  const nav = useNavigate();
  const userJson = localStorage.getItem("jb_platform_user");
  const user = userJson ? JSON.parse(userJson) : null;
  if (!localStorage.getItem("jb_platform_token")) {
    return <Navigate to="/admin/login" replace />;
  }
  const logout = () => {
    localStorage.removeItem("jb_platform_token");
    localStorage.removeItem("jb_platform_user");
    nav("/admin/login", { replace: true });
  };

  const linkCls = ({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded text-sm transition-colors ${isActive ? "bg-rose-600 text-white" : "text-slate-300 hover:bg-slate-800"}`;

  return (
    <div className={BG}>
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-6 w-6 text-rose-400" />
            <div>
              <div className="mono text-[9px] uppercase tracking-[0.3em] text-rose-400">JobsBook Platform</div>
              <div className="text-sm font-bold">Super-admin console</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-1" data-testid="admin-nav">
            <NavLink to="/admin" end className={linkCls} data-testid="admin-nav-dashboard"><LayoutDashboard className="h-4 w-4" /> Dashboard</NavLink>
            <NavLink to="/admin/companies" className={linkCls} data-testid="admin-nav-companies"><Building2 className="h-4 w-4" /> Companies</NavLink>
            <NavLink to="/admin/archived-trials" className={linkCls} data-testid="admin-nav-archived-trials"><Archive className="h-4 w-4" /> Archived trials</NavLink>
            <NavLink to="/admin/email-templates" className={linkCls} data-testid="admin-nav-email-templates"><Mail className="h-4 w-4" /> Email templates</NavLink>
            <NavLink to="/admin/invoice-template" className={linkCls} data-testid="admin-nav-invoice-template"><FileText className="h-4 w-4" /> Invoice template</NavLink>
            <NavLink to="/admin/email-config" className={linkCls} data-testid="admin-nav-email-config"><Mail className="h-4 w-4" /> Email config</NavLink>
          </nav>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">{user?.email}</span>
            <Button variant="outline" size="sm" onClick={logout} className="border-slate-700 text-slate-200 bg-slate-900 hover:bg-slate-800" data-testid="admin-logout">
              <LogOut className="h-3.5 w-3.5 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}

export function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  useEffect(() => {
    Promise.all([
      platformApi.get("/platform/stats"),
      platformApi.get("/platform/companies"),
    ]).then(([s, c]) => { setStats(s.data); setCompanies(c.data); }).catch(() => {});
  }, []);

  const Stat = ({ label, value, testid }) => (
    <div className="bg-slate-900 border border-slate-800 rounded-[8px] p-5" data-testid={testid}>
      <div className="mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{label}</div>
      <div className="text-3xl font-extrabold mt-1">{value}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-rose-400">// Overview</div>
        <h1 className="text-3xl font-black mt-1">Platform dashboard</h1>
      </div>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
          <Stat label="Companies" value={stats.companies_total} testid="admin-stat-companies" />
          <Stat label="Active" value={stats.companies_active} testid="admin-stat-active" />
          <Stat label="Suspended" value={stats.companies_suspended} testid="admin-stat-suspended" />
          <Stat label="Expiring ≤7d" value={stats.companies_expiring_soon ?? 0} testid="admin-stat-expiring-soon" />
          <Stat label="In grace" value={stats.companies_in_grace ?? 0} testid="admin-stat-in-grace" />
          <Stat label="Frozen" value={stats.companies_frozen ?? 0} testid="admin-stat-frozen" />
          <Stat label="Total Bookings" value={stats.bookings_total ?? 0} testid="admin-stat-bookings" />
          <Stat label="Total Invoices" value={stats.invoices_total ?? 0} testid="admin-stat-invoices" />
          <Stat label="Revenue (paid)" value={`€${(stats.revenue_paid_total || 0).toFixed(2)}`} testid="admin-stat-revenue" />
        </div>
      )}
      <section className="bg-slate-900 border border-slate-800 rounded-[8px] overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
          <div className="font-bold text-sm uppercase tracking-widest">Recent companies</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-950/50 text-slate-400">
            <tr>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Owner</th>
              <th className="text-right px-4 py-2">Bookings</th>
              <th className="text-right px-4 py-2">Customers</th>
              <th className="text-left px-4 py-2">Created</th>
            </tr>
          </thead>
          <tbody>
            {companies.slice(0, 10).map((c) => (
              <tr key={c.id} className="border-t border-slate-800">
                <td className="px-4 py-2 font-bold">{c.name}{c.archived && <span className="ml-2 text-[10px] uppercase mono bg-rose-900/50 text-rose-300 px-1.5 py-0.5 rounded">Suspended</span>}</td>
                <td className="px-4 py-2 text-slate-400 mono text-xs">{c.owner_email}</td>
                <td className="px-4 py-2 text-right mono">{c.booking_count}</td>
                <td className="px-4 py-2 text-right mono">{c.customer_count}</td>
                <td className="px-4 py-2 text-slate-500 mono text-xs">{(c.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
